// =============================================================================
// screens/CoinDetailScreen.tsx — token detail (route: CoinDetail, IMG_5882).
//
// Structure adopted from the old web coin page, recolored to the lavender glass
// system: price header (logo, name, category badge, big price, 24h change) →
// Website / White Paper buttons → price chart with range tabs → Join Community
// CTA (only when a matching CRYB community exists) → market stats grid → About
// → related Learn articles.
//
// Ranges: the API history endpoint serves 24h/7d/30d/1y, so 1D/1W/1M/1Y are
// live and 1H/All render visible-but-disabled (dimmed) until the backend grows
// those ranges (services/markets CHART_RANGES).
//
// v1 crypto policy: this screen is INFO-ONLY (viewing market data is v1-safe).
// There is deliberately no buy/sell/swap affordance here.
//
// REGISTRATION (orchestrator, App.tsx root stack):
//   <RootStack.Screen name="CoinDetail" component={CoinDetailScreen}
//     options={{ ...headerStyle, title: 'Coin' }} />
// =============================================================================
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Linking,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RouteProp } from '@react-navigation/native';
import { Ionicons, IoniconName } from '../components/ui/Icon';
import ParticleBackground from '../components/ui/ParticleBackground';
import Asset3D from '../components/ui/Asset3D';
import ProgressRow from '../components/ui/ProgressRow';
import { GlassCard, GlassSurface, glassTokens } from '../components/glass';
import Sparkline from '../components/markets/Sparkline';
import CoinIcon from '../components/markets/CoinIcon';
import {
  TokenDetail,
  ChartRange,
  CHART_RANGES,
  fetchTokenDetail,
  fetchTokenHistory,
  formatPrice,
  formatLargeNumber,
  formatSupply,
  matchCommunityForCoin,
} from '../services/markets';
import { api } from '../services/api';
import { tabularNums, ticker } from '../lib/format';
import {
  colors,
  spacing,
  radius,
  fonts,
  typographyScale,
  shadowTokens,
  colorTokens,
  getLayout,
  layout as layoutTokens,
} from '../constants/theme';
import type { RootStackParamList } from '../navigation/types';

type Nav = NativeStackNavigationProp<RootStackParamList, 'CoinDetail'>;
type Rt = RouteProp<RootStackParamList, 'CoinDetail'>;

// Chart card inner height.
const CHART_HEIGHT = 160;

// Content column cap so the page never stretches edge-to-edge on tablet/desktop.
// Mirrors the caps in components/ui/Screen (desktop 720 / tablet 620) so the
// chart width tracks the actual constrained column instead of overflowing it.
function columnCap(width: number): number {
  const l = getLayout(width);
  if (l === 'desktop') return Math.min(width, 720);
  if (l === 'tablet') return Math.min(width, 620);
  return width;
}

// Semantic tints derived from the brand palette (no ad-hoc rgba scattered per
// style). Used for badges, pressed slabs and directional change chips.
const TINT = {
  primarySoft: 'rgba(108,92,231,0.12)',
  successSoft: 'rgba(45,181,93,0.12)',
  errorSoft: 'rgba(255,71,87,0.10)',
} as const;

// Open an external link crash-safely (bad/unsupported URLs just no-op).
async function openUrl(url?: string | null) {
  if (!url) return;
  try { await Linking.openURL(url); } catch { /* unsupported URL — ignore */ }
}

function StatRow({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.statRow}>
      <Text style={styles.statRowLabel}>{label}</Text>
      <Text style={[styles.statRowValue, tabularNums]} numberOfLines={1}>{value}</Text>
    </View>
  );
}

function LinkButton({ label, icon, url }: { label: string; icon: IoniconName; url: string }) {
  return (
    <GlassCard
      style={styles.linkBtn}
      contentStyle={styles.linkBtnContent}
      padding={spacing.sm + 2}
      onPress={() => openUrl(url)}
      accessibilityRole="button"
      accessibilityLabel={label}
    >
      <Ionicons name={icon} size={15} color={colors.primary} />
      <Text style={styles.linkBtnText}>{label}</Text>
    </GlassCard>
  );
}

export default function CoinDetailScreen() {
  const navigation = useNavigation<Nav>();
  const route = useRoute<Rt>();
  const insets = useSafeAreaInsets();
  const symbol = (route.params?.symbol || '').toUpperCase();
  const { width } = useWindowDimensions();

  // Self-manage the header (skill §12): hide the opaque native bar so the aurora
  // shows through the status bar and content scrolls under an in-screen glass
  // header. Runs before paint to avoid a flash of the native bar.
  useLayoutEffect(() => {
    navigation.setOptions({ headerShown: false });
  }, [navigation]);

  const [detail, setDetail] = useState<TokenDetail | null>(null);
  const [reloadTick, setReloadTick] = useState(0); // retry re-runs the WHOLE load (detail + community + articles)
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<ChartRange>(CHART_RANGES[1]); // 1D default
  const [series, setSeries] = useState<number[]>([]);
  const [chartLoading, setChartLoading] = useState(true);
  const [community, setCommunity] = useState<any | null>(null);
  const [articles, setArticles] = useState<any[]>([]);
  // Per-range series cache so tab flips don't refetch.
  const seriesCache = useRef<Record<string, number[]>>({});

  // Native header title tracks the coin.
  useEffect(() => {
    navigation.setOptions({ title: route.params?.name || symbol || 'Coin' });
  }, [navigation, route.params?.name, symbol]);

  // ---- Detail + side content --------------------------------------------------
  useEffect(() => {
    let active = true;
    (async () => {
      const d = await fetchTokenDetail(symbol);
      if (!active) return;
      setDetail(d);
      setLoading(false);
      if (d?.name) navigation.setOptions({ title: d.name });
    })();

    // CRYB community match (hide the CTA when none) — /communities is the same
    // endpoint the directory uses; wrapped defensively.
    (async () => {
      const res = await api<any>('/communities');
      if (!active || !res.success) return;
      const items = Array.isArray(res.data?.items) ? res.data.items
        : Array.isArray(res.data?.communities) ? res.data.communities
        : Array.isArray(res.data) ? res.data : [];
      setCommunity(matchCommunityForCoin(items, symbol, route.params?.name || ''));
    })();

    // Related Learn articles: filter the catalog by coin name/symbol mention.
    // Omitted entirely when nothing matches (no dead sections).
    (async () => {
      const res = await api<any>('/learn?limit=100');
      if (!active || !res.success) return;
      const d: any = res.data ?? {};
      const list: any[] = d.articles ?? d.items ?? (Array.isArray(d) ? d : []);
      const name = (route.params?.name || '').toLowerCase();
      const sym = symbol.toLowerCase();
      const matches = (Array.isArray(list) ? list : []).filter((a) => {
        const hay = `${a?.title ?? ''} ${a?.description ?? ''} ${a?.excerpt ?? ''} ${a?.category ?? ''}`.toLowerCase();
        return (name && hay.includes(name)) || (sym && new RegExp(`\\b${sym}\\b`).test(hay));
      });
      setArticles(matches.slice(0, 6));
    })();

    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, reloadTick]);

  // ---- Chart series -------------------------------------------------------------
  const loadSeries = useCallback(async (r: ChartRange, sparkline7d?: number[]) => {
    if (!r.tf) return;
    const cached = seriesCache.current[r.tf];
    if (cached) { setSeries(cached); setChartLoading(false); return; }
    setChartLoading(true);
    const rows = await fetchTokenHistory(symbol, r.tf);
    let prices = rows.map((p) => p.price);
    // 1W fallback: the detail payload carries CoinGecko's real 7d sparkline.
    if (prices.length < 2 && r.tf === '7d' && sparkline7d && sparkline7d.length >= 2) {
      prices = sparkline7d;
    }
    seriesCache.current[r.tf] = prices;
    setSeries(prices);
    setChartLoading(false);
  }, [symbol]);

  useEffect(() => {
    loadSeries(range, detail?.sparkline7d);
  }, [range, detail?.sparkline7d, loadSeries]);

  const price = detail?.price;
  const change = detail?.change24h ?? 0;
  const up = change >= 0;
  const category = detail?.categories?.[0];
  // Chart subtracts the page gutter (card margin) and the card's inner padding
  // on both sides, tracking the constrained column so it never overflows.
  const chartWidth = columnCap(width) - spacing.lg * 2 - spacing.lg * 2;
  const hasChart = series.length >= 2;
  const chartMin = hasChart ? Math.min(...series) : 0;
  const chartMax = hasChart ? Math.max(...series) : 0;

  // Cap + centre the scrolling column on tablet/desktop (Screen used to own this).
  const cLayout = getLayout(width);
  const containerStyle = cLayout !== 'phone'
    ? { maxWidth: cLayout === 'desktop' ? 720 : 620, alignSelf: 'center' as const, width: '100%' as const }
    : undefined;

  // 24h position: where the live price sits within the day's low→high band. Feeds
  // a ProgressRow so the stat reads as a physical range, not just a number pair.
  const lo = detail?.low24h;
  const hi = detail?.high24h;
  const dayPos = (typeof lo === 'number' && typeof hi === 'number' && typeof price === 'number' && hi > lo)
    ? Math.max(0, Math.min(1, (price - lo) / (hi - lo)))
    : null;

  const showLinks = !!(detail?.websiteUrl || detail?.whitepaperUrl);

  // In-screen translucent header (skill §12): back chevron + coin name (tracks
  // the loaded detail) + optional rank chip. Content scrolls underneath it, so
  // the aurora is visible through the status bar with no opaque strip.
  const headerTitle = detail?.name || route.params?.name || symbol || 'Coin';
  const header = (
    <GlassSurface
      variant="bar"
      mode="light"
      radius={0}
      style={styles.header}
      contentStyle={[styles.headerContent, { paddingTop: insets.top }]}
    >
      <TouchableOpacity
        onPress={() => navigation.goBack()}
        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        accessibilityRole="button"
        accessibilityLabel="Go back"
      >
        <Ionicons name="chevron-back" size={24} color={colors.ink} />
      </TouchableOpacity>
      <View style={styles.headerTitleWrap}>
        {detail ? (
          <CoinIcon symbol={detail.symbol} image={detail.image || route.params?.image} size={22} />
        ) : null}
        <Text style={styles.headerTitle} numberOfLines={1}>{headerTitle}</Text>
      </View>
      {detail?.rank != null ? (
        <View style={styles.headerRank}>
          <Text style={styles.headerRankText}>#{detail.rank}</Text>
        </View>
      ) : (
        <View style={styles.headerRightSpacer} />
      )}
    </GlassSurface>
  );

  if (loading) {
    return (
      <View style={styles.screen}>
        <ParticleBackground style={StyleSheet.absoluteFill} theme="light" count={5} />
        {header}
        <View style={styles.loadingWrap}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </View>
    );
  }

  if (!detail) {
    return (
      <View style={styles.screen}>
        <ParticleBackground style={StyleSheet.absoluteFill} theme="light" count={5} />
        {header}
        <View style={styles.loadingWrap}>
          <Asset3D name="error" size={120} style={{ marginBottom: spacing.sm }} />
          <Text style={styles.errorTitle}>Couldn't load {symbol || 'this coin'}</Text>
          <Text style={styles.errorSub}>Check your connection and try again.</Text>
          <TouchableOpacity
            style={styles.retryBtn}
            onPress={() => {
              setLoading(true);
              setReloadTick((t) => t + 1); // re-runs detail + community + articles, not just price
            }}
            accessibilityRole="button"
            accessibilityLabel="Retry"
          >
            <Ionicons name="refresh-outline" size={15} color={colors.white} />
            <Text style={styles.retryText}>Retry</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.screen}>
      {/* Full-bleed aurora under the status bar + translucent header (skill §12). */}
      <ParticleBackground style={StyleSheet.absoluteFill} theme="light" count={5} />
      {header}

      <ScrollView
        style={styles.flex}
        contentContainerStyle={[styles.content, containerStyle]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
      {/* ── Price header ── */}
      <View style={styles.identityRow}>
        <CoinIcon symbol={detail.symbol} image={detail.image || route.params?.image} size={48} />
        <View style={styles.headerText}>
          <View style={styles.nameRow}>
            <Text style={styles.coinName} numberOfLines={1}>{detail.name}</Text>
            <Text style={styles.coinSymbol}>{ticker(detail.symbol)}</Text>
          </View>
          {category ? (
            <View style={styles.categoryBadge}>
              <Text style={styles.categoryText} numberOfLines={1}>{category}</Text>
            </View>
          ) : null}
        </View>
      </View>

      <View style={styles.priceRow}>
        <Text style={[styles.price, tabularNums]}>{formatPrice(price)}</Text>
        <View style={[styles.changeBadge, { backgroundColor: up ? TINT.successSoft : TINT.errorSoft }]}>
          <Ionicons name={up ? 'caret-up' : 'caret-down'} size={11} color={up ? colors.success : colors.error} />
          <Text style={[styles.changeText, tabularNums, { color: up ? colors.success : colors.error }]}>
            {Math.abs(change).toFixed(2)}% (24h)
          </Text>
        </View>
      </View>

      {/* ── Website / White Paper (info links, IMG_5882) ── */}
      {showLinks ? (
        <View style={styles.linksRow}>
          {detail.websiteUrl ? <LinkButton label="Website" icon="globe-outline" url={detail.websiteUrl} /> : null}
          {detail.whitepaperUrl ? <LinkButton label="White Paper" icon="document-text-outline" url={detail.whitepaperUrl} /> : null}
        </View>
      ) : null}

      {/* ── Price chart ── */}
      <GlassCard style={styles.chartCard} contentStyle={styles.chartCardContent}>
        <Text style={styles.sectionTitle}>Price Chart</Text>
        <View style={styles.rangeRow}>
          {CHART_RANGES.map((r) => {
            const enabled = !!r.tf;
            const active = r.label === range.label;
            return (
              <TouchableOpacity
                key={r.label}
                style={[styles.rangeTab, active && styles.rangeTabActive, !enabled && styles.rangeTabDisabled]}
                onPress={() => enabled && setRange(r)}
                disabled={!enabled}
                accessibilityRole="button"
                accessibilityState={{ selected: active, disabled: !enabled }}
                accessibilityLabel={enabled ? `Show ${r.label} chart` : `${r.label} range coming soon`}
              >
                <Text style={[styles.rangeText, active && styles.rangeTextActive, !enabled && styles.rangeTextDisabled]}>
                  {r.label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>

        {chartLoading ? (
          <View style={[styles.chartEmpty, { height: CHART_HEIGHT }]}>
            <ActivityIndicator color={colors.primary} />
          </View>
        ) : hasChart ? (
          <View>
            <Sparkline
              data={series}
              width={chartWidth}
              height={CHART_HEIGHT}
              strokeWidth={2.25}
              color={colors.primary}
              area
              endDot
            />
            <View style={styles.chartAxisRow}>
              <Text style={styles.chartAxisText}>Low {formatPrice(chartMin)}</Text>
              <Text style={styles.chartAxisText}>High {formatPrice(chartMax)}</Text>
            </View>
          </View>
        ) : (
          <View style={[styles.chartEmpty, { height: CHART_HEIGHT }]}>
            <Asset3D name="chart" size={72} />
            <Text style={styles.chartEmptyText}>Chart data unavailable right now</Text>
          </View>
        )}
      </GlassCard>

      {/* ── Join Community (hidden when no CRYB community matches) ── */}
      {community ? (
        <GlassCard
          style={styles.communityCard}
          contentStyle={styles.communityContent}
          onPress={() => navigation.navigate('Community', { name: community.name })}
          accessibilityRole="button"
          accessibilityLabel={`Open the ${community.displayName || community.name} community`}
        >
          <View style={styles.communityIcon}>
            <Ionicons name="people" size={18} color={colors.white} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.communityTitle} numberOfLines={1}>
              {community.displayName || community.name}
            </Text>
            <Text style={styles.communitySub} numberOfLines={1}>
              {typeof community.memberCount === 'number'
                ? `${community.memberCount.toLocaleString()} members on CRYB`
                : 'The home for this coin on CRYB'}
            </Text>
          </View>
          <View style={styles.communityCta}>
            <Text style={styles.communityCtaText}>Join</Text>
            <Ionicons name="chevron-forward" size={14} color={colors.white} />
          </View>
        </GlassCard>
      ) : null}

      {/* ── Market stats ── */}
      <GlassCard style={styles.statsCard} contentStyle={styles.statsContent}>
        <Text style={styles.sectionTitle}>Market Stats</Text>
        {/* 24h range as a physical band: low ─●─ high, the dot at today's price. */}
        {dayPos != null ? (
          <ProgressRow
            label="24h Range"
            progress={dayPos}
            value={`${formatPrice(lo)} – ${formatPrice(hi)}`}
            showPercent={false}
            color={up ? colors.success : colors.error}
            style={styles.rangeBar}
          />
        ) : null}
        <StatRow label="Market Cap" value={formatLargeNumber(detail.marketCap)} />
        <StatRow label="24h Volume" value={formatLargeNumber(detail.volume24h)} />
        <StatRow label="24h High" value={formatPrice(detail.high24h)} />
        <StatRow label="24h Low" value={formatPrice(detail.low24h)} />
        <StatRow label="Circulating Supply" value={formatSupply(detail.circulatingSupply, detail.symbol)} />
        {detail.change7d != null ? (
          <StatRow label="7d Change" value={`${detail.change7d >= 0 ? '+' : ''}${detail.change7d.toFixed(2)}%`} />
        ) : null}
      </GlassCard>

      {/* ── About ── */}
      {detail.description ? (
        <GlassCard style={styles.aboutCard} contentStyle={styles.statsContent}>
          <Text style={styles.sectionTitle}>About {detail.name}</Text>
          <Text style={styles.aboutText} numberOfLines={8}>{detail.description}</Text>
        </GlassCard>
      ) : null}

      {/* ── Related Learn articles (omitted when nothing matches) ── */}
      {articles.length > 0 ? (
        <View style={styles.articlesSection}>
          <Text style={[styles.sectionTitle, styles.articlesTitle]}>Learn about {detail.name}</Text>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.articlesRow}
          >
            {articles.map((a, i) => (
              <GlassCard
                key={a?.slug ?? a?.id ?? i}
                style={styles.articleCard}
                contentStyle={styles.articleContent}
                onPress={() => a?.slug && navigation.navigate('LearnArticle', { slug: String(a.slug) })}
                accessibilityRole="button"
                accessibilityLabel={`Read ${a?.title ?? 'article'}`}
              >
                {a?.category ? (
                  <View style={styles.articleBadge}>
                    <Text style={styles.articleBadgeText}>{String(a.category).toUpperCase()}</Text>
                  </View>
                ) : null}
                <Text style={styles.articleTitle} numberOfLines={3}>{a?.title ?? 'Article'}</Text>
                <View style={styles.articleFooter}>
                  <Text style={styles.articleRead}>Read</Text>
                  <Ionicons name="arrow-forward" size={12} color={colors.primary} />
                </View>
              </GlassCard>
            ))}
          </ScrollView>
        </View>
      ) : null}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: colors.bg },
  flex: { flex: 1 },
  content: {
    // Clears the floating translucent header (safe-area is added inline) and the
    // floating glass tab bar at the bottom so nothing hides under either edge.
    paddingTop: spacing.md,
    paddingBottom: layoutTokens.bottomNavInset,
  },

  // --- In-screen translucent header (skill §12) ---
  header: {
    // Full-bleed bar; radius 0 so it reads as chrome, content scrolls under it.
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.sm + 2,
    minHeight: 44,
  },
  headerTitleWrap: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  headerTitle: { ...typographyScale.h3, ...fonts.semibold, color: colors.ink, flexShrink: 1 },
  headerRank: {
    backgroundColor: 'rgba(255,255,255,0.7)',
    borderRadius: radius.full,
    paddingHorizontal: spacing.sm + 2,
    paddingVertical: 4,
    borderWidth: 1,
    borderColor: glassTokens.border,
  },
  headerRankText: { ...fonts.semibold, fontSize: 12, color: colors.ink2 },
  headerRightSpacer: { width: 24 },

  loadingWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    padding: spacing.xl,
  },
  errorTitle: { ...fonts.semibold, fontSize: 16, color: colors.ink, marginTop: spacing.sm },
  errorSub: { ...fonts.regular, fontSize: 13, color: colors.muted },
  retryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm + 2,
    borderRadius: radius.full,
    marginTop: spacing.md,
    minHeight: 44,
  },
  retryText: { ...fonts.semibold, fontSize: 14, color: colors.white },

  identityRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingHorizontal: spacing.lg,
  },
  headerText: { flex: 1, minWidth: 0 },
  nameRow: { flexDirection: 'row', alignItems: 'baseline', gap: spacing.sm },
  coinName: { ...typographyScale.h2, ...fonts.extrabold, color: colorTokens.light.text, flexShrink: 1 },
  coinSymbol: { ...fonts.semibold, fontSize: 13, color: colors.muted },
  categoryBadge: {
    alignSelf: 'flex-start',
    backgroundColor: TINT.primarySoft,
    borderRadius: radius.full,
    paddingHorizontal: spacing.sm + 2,
    paddingVertical: 3,
    marginTop: spacing.xs,
  },
  categoryText: { ...fonts.semibold, fontSize: 11, color: colors.primary },

  priceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingHorizontal: spacing.lg,
    marginTop: spacing.md,
  },
  // Tighten the large numeral (skill §15): negative tracking, hierarchy by weight.
  price: { ...typographyScale.h1, ...fonts.extrabold, letterSpacing: -0.6, color: colorTokens.light.text },
  changeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: radius.sm,
  },
  changeText: { ...fonts.semibold, fontSize: 12 },

  linksRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    paddingHorizontal: spacing.lg,
    marginTop: spacing.lg,
  },
  linkBtn: { flexShrink: 1 },
  linkBtnContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
  },
  linkBtnText: { ...fonts.semibold, fontSize: 13, color: colors.primary },

  chartCard: { marginHorizontal: spacing.lg, marginTop: spacing.lg, ...shadowTokens.card },
  chartCardContent: { gap: spacing.sm },
  sectionTitle: { ...typographyScale.h3, ...fonts.bold, color: colorTokens.light.text },
  rangeRow: {
    flexDirection: 'row',
    gap: spacing.xs,
    flexWrap: 'wrap',
  },
  rangeTab: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs + 2,
    borderRadius: radius.full,
    backgroundColor: glassTokens.surface.pill,
    borderWidth: 1,
    borderColor: glassTokens.border,
  },
  rangeTabActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  // Visible-but-disabled: ranges the API doesn't serve yet (1H / All).
  rangeTabDisabled: { opacity: 0.35 },
  rangeText: { ...fonts.semibold, fontSize: 12, color: colors.ink2 },
  rangeTextActive: { color: colors.white },
  rangeTextDisabled: { color: colors.muted },
  chartEmpty: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: glassTokens.borderSubtle,
    backgroundColor: TINT.primarySoft,
  },
  chartEmptyText: { ...fonts.medium, fontSize: 12, color: colors.muted },
  chartAxisRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: spacing.xs,
  },
  chartAxisText: { ...fonts.medium, fontSize: 11, color: colors.muted },

  communityCard: { marginHorizontal: spacing.lg, marginTop: spacing.lg, ...shadowTokens.card },
  communityContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  communityIcon: {
    width: 38,
    height: 38,
    borderRadius: radius.full,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  communityTitle: { ...fonts.semibold, fontSize: 14, color: colors.ink },
  communitySub: { ...fonts.medium, fontSize: 12, color: colors.muted, marginTop: 1 },
  communityCta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    backgroundColor: colors.primary,
    borderRadius: radius.full,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs + 3,
  },
  communityCtaText: { ...fonts.semibold, fontSize: 13, color: colors.white },

  statsCard: { marginHorizontal: spacing.lg, marginTop: spacing.lg, ...shadowTokens.card },
  statsContent: { gap: spacing.sm },
  rangeBar: { marginBottom: spacing.xs },
  statRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: spacing.md,
  },
  statRowLabel: { ...fonts.medium, fontSize: 13, color: colors.muted },
  statRowValue: { ...fonts.semibold, fontSize: 13, color: colors.ink, flexShrink: 1 },

  aboutCard: { marginHorizontal: spacing.lg, marginTop: spacing.lg, ...shadowTokens.card },
  aboutText: { ...fonts.regular, fontSize: 13, lineHeight: 20, color: colors.ink2 },

  articlesSection: { marginTop: spacing.lg },
  articlesTitle: { paddingHorizontal: spacing.lg, marginBottom: spacing.sm },
  articlesRow: {
    paddingHorizontal: spacing.lg,
    gap: spacing.sm,
  },
  articleCard: { width: 180, ...shadowTokens.card },
  articleContent: { gap: spacing.sm, minHeight: 120 },
  articleBadge: {
    alignSelf: 'flex-start',
    backgroundColor: TINT.primarySoft,
    borderRadius: radius.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
  },
  articleBadgeText: { ...fonts.bold, fontSize: 9, color: colors.primary, letterSpacing: 0.5 },
  articleTitle: { ...fonts.semibold, fontSize: 13, lineHeight: 18, color: colors.ink, flex: 1 },
  articleFooter: { flexDirection: 'row', alignItems: 'center', gap: 3 },
  articleRead: { ...fonts.semibold, fontSize: 12, color: colors.primary },
});
